import React from "react";
import { Link } from "react-router-dom";
import PublicHeader from "../components/layouts/PublicHeader";
import PublicFooter from "../components/layouts/PublicFooter";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import { 
  QrCode, Smartphone, CreditCard, ChefHat, BarChart3, Clock,
  ArrowRight, CheckCircle, Users, Zap, Shield, Globe
} from "lucide-react";

const LandingPage = () => {
  const features = [
    {
      icon: QrCode,
      title: "QR Code Ordering",
      description: "Customers scan, browse menu, and order instantly from their phones. No app download needed.",
    },
    {
      icon: Smartphone,
      title: "Mobile-First Design",
      description: "Optimized for smartphones and mobile networks across Somalia.",
    },
    {
      icon: CreditCard,
      title: "Mobile Money Payments",
      description: "Accept EVC Plus, Zaad, and E-Dahab payments seamlessly.",
    },
    {
      icon: ChefHat,
      title: "Kitchen Display",
      description: "Real-time Kanban board for efficient order management and tracking.",
    },
    {
      icon: BarChart3,
      title: "Sales Analytics",
      description: "Track revenue, orders, and export reports to CSV for accounting.",
    },
    {
      icon: Shield,
      title: "Secure & Reliable",
      description: "Enterprise-grade security with data isolation for each restaurant.",
    },
  ];

  const steps = [
    { 
      step: "1", 
      title: "Sign Up", 
      description: "Create your restaurant account in minutes. No technical skills required.",
      icon: Users
    },
    { 
      step: "2", 
      title: "Setup Menu", 
      description: "Add your menu items, set prices in USD, and customize your brand.",
      icon: ChefHat
    },
    { 
      step: "3", 
      title: "Print QR Codes", 
      description: "Generate and print QR codes for each table or your counter.",
      icon: QrCode
    },
  ];

  const benefits = {
    restaurants: [
      "Reduce ordering errors",
      "Speed up table turnover",
      "Track all orders digitally",
      "Accept mobile money instantly",
      "Real-time kitchen updates",
      "Daily sales reports",
    ],
    customers: [
      "Order from phone",
      "No waiting for service",
      "See full menu with photos",
      "Pay with mobile money",
      "Track order status",
      "No app installation needed",
    ],
  };

  const pricingPlans = [
    {
      name: "Starter",
      price: "39",
      period: "/month",
      description: "Perfect for small cafés and food kiosks",
      features: [
        "Up to 6 tables",
        "Unlimited orders",
        "Basic reports",
        "Mobile money payments",
        "Email support",
      ],
      notIncluded: [
        "Custom branding",
        "Logo upload",
      ],
      cta: "Start Free Trial",
      popular: false,
    },
    {
      name: "Professional",
      price: "69",
      period: "/month",
      description: "For growing restaurants",
      features: [
        "Unlimited tables",
        "Unlimited orders",
        "Advanced analytics",
        "All mobile money providers",
        "Priority support",
        "Multiple staff accounts",
        "Custom logo & branding",
        "Brand colors",
      ],
      notIncluded: [],
      cta: "Start Free Trial",
      popular: true,
    },
    {
      name: "Enterprise",
      price: "Custom",
      period: "",
      description: "For restaurant chains",
      features: [
        "Everything in Pro",
        "Multi-location support",
        "Custom integrations",
        "Dedicated account manager",
        "24/7 phone support",
        "White-label option",
      ],
      notIncluded: [],
      cta: "Contact Sales",
      popular: false,
    },
  ];

  return (
    <div className="min-h-screen bg-white">
      <PublicHeader />
      
      {/* Hero Section */}
      <section className="pt-24 pb-16 sm:pt-32 sm:pb-24 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-sky-50 via-white to-blue-50">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-sky-100 text-sky-700 rounded-full text-sm font-medium mb-6">
                <Zap className="w-4 h-4" />
                #1 QR Ordering Platform in Somalia — Dalabsom
              </div>
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-gray-900 tracking-tight leading-tight mb-6">
                Dalabsom —{" "}
                <span className="text-sky-500">Smart Ordering for Somali Restaurants</span>
              </h1>
              <p className="text-lg sm:text-xl text-gray-600 mb-8 max-w-lg leading-relaxed">
                Transform your restaurant with mobile-first QR ordering. Accept EVC Plus, Zaad & E-Dahab, 
                manage orders in real-time, and delight your customers.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <Link to="/login">
                  <Button size="lg" className="w-full sm:w-auto gap-2 bg-sky-500 hover:bg-sky-600 h-12 px-8 text-base">
                    Get Started Free
                    <ArrowRight className="w-5 h-5" />
                  </Button>
                </Link>
                <Link to="/r/java-cafe">
                  <Button size="lg" variant="outline" className="w-full sm:w-auto h-12 px-8 text-base">
                    View Live Demo
                  </Button>
                </Link>
              </div>
              <div className="mt-8 flex items-center gap-6 text-sm text-gray-500">
                <div className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-sky-500" />
                  <span>14-day free trial</span>
                </div>
                <div className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-sky-500" />
                  <span>No credit card required</span>
                </div>
              </div>
            </div>
            <div className="relative hidden lg:block">
              <div className="bg-white rounded-3xl shadow-2xl p-6 transform hover:scale-[1.02] transition-transform">
                <img 
                  src="https://images.unsplash.com/photo-1555396273-367ea4eb4db5?w=600&h=400&fit=crop"
                  alt="Restaurant interior"
                  className="rounded-2xl w-full h-64 object-cover"
                />
                <div className="mt-4 p-4 bg-gray-50 rounded-xl">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-gray-500">Today's Orders</p>
                      <p className="text-2xl font-bold text-gray-900">147</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-500">Revenue</p>
                      <p className="text-2xl font-bold text-sky-500">$1,250</p>
                    </div>
                  </div>
                </div>
              </div>
              <div className="absolute -bottom-4 -left-4 bg-sky-500 text-white p-4 rounded-2xl shadow-lg">
                <div className="flex items-center gap-2">
                  <CreditCard className="w-5 h-5" />
                  <span className="font-semibold">Mobile Money Ready</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Payment Methods Banner */}
      <section className="py-8 bg-gray-50 border-y border-gray-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-wrap items-center justify-center gap-8 sm:gap-12">
            <p className="text-gray-500 text-sm font-medium">Accepted payments:</p>
            <div className="flex items-center gap-3">
              <div className="px-4 py-2 bg-orange-500 rounded-lg">
                <span className="text-white font-bold text-sm">EVC Plus</span>
              </div>
              <div className="px-4 py-2 bg-green-600 rounded-lg">
                <span className="text-white font-bold text-sm">Zaad</span>
              </div>
              <div className="px-4 py-2 bg-blue-600 rounded-lg">
                <span className="text-white font-bold text-sm">E-Dahab</span>
              </div>
              <div className="px-4 py-2 bg-gray-200 rounded-lg">
                <span className="text-gray-700 font-bold text-sm">Cash</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-white" id="how-it-works">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">How It Works</h2>
            <p className="text-gray-600 max-w-2xl mx-auto text-lg">
              Get your restaurant up and running in less than a day
            </p>
          </div>
          <div className="grid md:grid-cols-3 gap-8">
            {steps.map((item, index) => (
              <div key={index} className="text-center relative">
                <div className="w-16 h-16 bg-sky-100 text-sky-500 rounded-2xl flex items-center justify-center mx-auto mb-6">
                  <item.icon className="w-8 h-8" />
                </div>
                <div className="w-8 h-8 bg-sky-500 text-white rounded-full flex items-center justify-center mx-auto mb-4 text-sm font-bold">
                  {item.step}
                </div>
                <h3 className="font-semibold text-xl mb-2 text-gray-900">{item.title}</h3>
                <p className="text-gray-600">{item.description}</p>
                {index < steps.length - 1 && (
                  <div className="hidden md:block absolute top-8 left-[60%] w-[80%] h-0.5 bg-sky-100" />
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Features Grid */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gray-50" id="features">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">Everything You Need</h2>
            <p className="text-gray-600 max-w-2xl mx-auto text-lg">
              A complete platform built specifically for Somali restaurants
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {features.map((feature, index) => (
              <Card key={index} className="border-0 shadow-sm hover:shadow-lg transition-all bg-white">
                <CardContent className="p-6">
                  <div className="w-12 h-12 bg-sky-100 rounded-xl flex items-center justify-center mb-4">
                    <feature.icon className="w-6 h-6 text-sky-500" />
                  </div>
                  <h3 className="font-semibold text-lg mb-2 text-gray-900">{feature.title}</h3>
                  <p className="text-gray-600 text-sm leading-relaxed">{feature.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">Benefits for Everyone</h2>
          </div>
          <div className="grid md:grid-cols-2 gap-12">
            <div className="bg-sky-50 rounded-2xl p-8">
              <h3 className="font-semibold text-xl mb-6 text-sky-800 flex items-center gap-2">
                <ChefHat className="w-6 h-6" />
                For Restaurants
              </h3>
              <ul className="space-y-3">
                {benefits.restaurants.map((benefit, i) => (
                  <li key={i} className="flex items-center gap-3 text-gray-700">
                    <CheckCircle className="w-5 h-5 text-sky-500 flex-shrink-0" />
                    <span>{benefit}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div className="bg-orange-50 rounded-2xl p-8">
              <h3 className="font-semibold text-xl mb-6 text-orange-800 flex items-center gap-2">
                <Users className="w-6 h-6" />
                For Customers
              </h3>
              <ul className="space-y-3">
                {benefits.customers.map((benefit, i) => (
                  <li key={i} className="flex items-center gap-3 text-gray-700">
                    <CheckCircle className="w-5 h-5 text-orange-500 flex-shrink-0" />
                    <span>{benefit}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Pricing */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gray-50" id="pricing">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">Simple, Transparent Pricing</h2>
            <p className="text-gray-600 max-w-2xl mx-auto text-lg">
              Start with a 14-day free trial. No credit card required.
            </p>
          </div>
          <div className="grid md:grid-cols-3 gap-8 max-w-5xl mx-auto">
            {pricingPlans.map((plan, index) => (
              <Card key={index} className={`relative ${plan.popular ? 'border-2 border-sky-500 shadow-xl' : 'border shadow-sm'}`}>
                {plan.popular && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-sky-500 text-white text-xs font-semibold px-3 py-1 rounded-full">
                    Most Popular
                  </div>
                )}
                <CardContent className="p-6">
                  <h3 className="font-semibold text-lg mb-2">{plan.name}</h3>
                  <div className="flex items-baseline gap-1 mb-2">
                    <span className="text-3xl font-bold text-gray-900">${plan.price}</span>
                    {plan.period && <span className="text-gray-500">{plan.period}</span>}
                  </div>
                  <p className="text-gray-600 text-sm mb-6">{plan.description}</p>
                  <ul className="space-y-3 mb-6">
                    {plan.features.map((feature, i) => (
                      <li key={i} className="flex items-center gap-2 text-sm text-gray-700">
                        <CheckCircle className="w-4 h-4 text-sky-500 flex-shrink-0" />
                        <span>{feature}</span>
                      </li>
                    ))}
                    {plan.notIncluded && plan.notIncluded.map((feature, i) => (
                      <li key={`no-${i}`} className="flex items-center gap-2 text-sm text-gray-400">
                        <span className="w-4 h-4 flex items-center justify-center text-gray-300">✕</span>
                        <span className="line-through">{feature}</span>
                      </li>
                    ))}
                  </ul>
                  <Link to="/login">
                    <Button className={`w-full ${plan.popular ? 'bg-sky-500 hover:bg-sky-600' : ''}`} variant={plan.popular ? 'default' : 'outline'}>
                      {plan.cta}
                    </Button>
                  </Link>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-sky-500">
        <div className="max-w-3xl mx-auto text-center">
          <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">
            Ready to Transform Your Restaurant?
          </h2>
          <p className="text-sky-100 mb-8 text-lg">
            Join restaurants across Somalia already using Dalabsom
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/login">
              <Button size="lg" variant="secondary" className="gap-2 h-12 px-8">
                Get Started Free
                <ArrowRight className="w-5 h-5" />
              </Button>
            </Link>
            <Link to="/r/java-cafe">
              <Button size="lg" variant="outline" className="h-12 px-8 bg-transparent border-white text-white hover:bg-white hover:text-sky-500">
                Try Demo
              </Button>
            </Link>
          </div>
        </div>
      </section>

      <PublicFooter />
    </div>
  );
};

export default LandingPage;
